import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import "./ActionTable.css";
import heartLoading from "../../assests/heartLoading.gif";
import IncidentPieChart from "./IncidentPieChart";
import IncidentTable from "./IncidentTable";
import { RotateCcw, Filter } from "lucide-react";
import ReusableFilterDrawer from "../../component/common/ReusableFilterDrawer";
import CustomMultiSelect from "../../component/Layout/DataContract/Viewkpi/CustomMultiSelect";
import AppliedFilters from "../../component/common/AppliedFilters";

const formatIncidentCategory = (category = "") => {
  if (!category) return "";
  return category.trim().toLowerCase() === "results unavailable"
    ? "SLA Breach"
    : category;
};

const INITIAL_SIZE = 80;
const MORE_SIZE = 40;

const ActionTable = () => {
  const [selectedParentDomain, setSelectedParentDomain] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState([]);
  const [selectedSubDomain, setSelectedSubDomain] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  const [searchValue, setSearchValue] = useState("");
  const [resolutionPendingStatus, setResolutionPendingStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [incidents, setIncidents] = useState([]);
  const [incidentCategories, setIncidentCategories] = useState([]);
  const [incidentCategoryCounts, setIncidentCategoryCounts] = useState({});
  const [filters, setFilters] = useState(null);

  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const loadMorePageRef = useRef(Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1);
  const loadedCountRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [pendingFetch, setPendingFetch] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [columnSearch, setColumnSearch] = useState({
    snowId: "",
    incidentDescription: "",
    incidentCategory: "",
    assignedTo: "",
    status: "",
    resolutionPendingStatus: "",
    createdAt: "",
  });

  const allParentDomains = useMemo(() => {
    if (!filters) return [];
    return filters.parent_domains || [];
  }, [filters]);

  const allDomains = useMemo(() => {
    if (!filters) return [];
    const domains = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        domains.push({ ...d, parentDomain: pd.parent_domain });
      });
    });
    return domains;
  }, [filters]);

  const allSubDomains = useMemo(() => {
    if (!filters) return [];
    const subDomains = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        d.sub_domains?.forEach((sd) => {
          subDomains.push({
            ...sd,
            parentDomain: pd.parent_domain,
            domain: d.domain,
          });
        });
      });
    });
    return subDomains;
  }, [filters]);

  const allProducts = useMemo(() => {
    if (!filters) return [];
    const products = [];
    filters.parent_domains?.forEach((pd) => {
      pd.domains?.forEach((d) => {
        d.sub_domains?.forEach((sd) => {
          sd.products?.forEach((p) => {
            products.push({
              name: p,
              parentDomain: pd.parent_domain,
              domain: d.domain,
              subDomain: sd.sub_domain,
            });
          });
        });
      });
    });
    return products;
  }, [filters]);

  const filterSections = useMemo(() => [{
    fields: [
      {
        type: "custom", key: "parentDomain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          const opts = allParentDomains.map((pd) => ({ value: pd.parent_domain, label: pd.parent_domain }));
          const currentValue = opts.filter((o) => (localFilters.parentDomain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Parent Domain"
              options={opts}
              value={currentValue}
              placeholder="All Parent Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, parentDomain: vals, domain: [], subDomain: [], product: [] }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "domain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allDomains;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((d) => localFilters.parentDomain.includes(d.parentDomain));
          const unique = Array.from(new Map(filtered.map((d) => [d.domain, d])).values());
          const opts = unique.map((d) => ({ value: d.domain, label: d.domain }));
          const currentValue = opts.filter((o) => (localFilters.domain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Domain"
              options={opts}
              value={currentValue}
              placeholder="All Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, domain: vals, subDomain: [], product: [] }));
              }}
            />
          );
        },
      },









      
      {
        type: "custom", key: "subDomain", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allSubDomains;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((sd) => localFilters.parentDomain.includes(sd.parentDomain));
          if (localFilters.domain?.length) filtered = filtered.filter((sd) => localFilters.domain.includes(sd.domain));
          const unique = Array.from(new Map(filtered.map((sd) => [sd.sub_domain, sd])).values());
          const opts = unique.map((sd) => ({ value: sd.sub_domain, label: sd.sub_domain }));
          const currentValue = opts.filter((o) => (localFilters.subDomain || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Sub Domain"
              options={opts}
              value={currentValue}
              placeholder="All Sub Domains"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, subDomain: vals, product: [] }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "product", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          let filtered = allProducts;
          if (localFilters.parentDomain?.length) filtered = filtered.filter((p) => localFilters.parentDomain.includes(p.parentDomain));
          if (localFilters.domain?.length) filtered = filtered.filter((p) => localFilters.domain.includes(p.domain));
          if (localFilters.subDomain?.length) filtered = filtered.filter((p) => localFilters.subDomain.includes(p.subDomain));
          const unique = Array.from(new Map(filtered.map((p) => [p.name, p])).values());
          const opts = unique.map((p) => ({ value: p.name, label: p.name }));
          const currentValue = opts.filter((o) => (localFilters.product || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Product"
              options={opts}
              value={currentValue}
              placeholder="All Products"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, product: vals }));
              }}
            />
          );
        },
      },
      {
        type: "custom", key: "category", defaultValue: [],
        render: (localFilters, setLocalFilters) => {
          const opts = incidentCategories.map((c) => ({ value: c, label: formatIncidentCategory(c) }));
          const currentValue = opts.filter((o) => (localFilters.category || []).includes(o.value));
          return (
            <CustomMultiSelect
              label="Incident Category"
              options={opts}
              value={currentValue}
              placeholder="All Categories"
              onChange={(_, v) => {
                const vals = (v || []).map((o) => o.value);
                setLocalFilters((prev) => ({ ...prev, category: vals }));
              }}
            />
          );
        },
      },
      { type: "date", key: "startDate", label: "Start Date", defaultValue: null },
      { type: "date", key: "endDate", label: "End Date", defaultValue: null },
    ],
  }], [allParentDomains, allDomains, allSubDomains, allProducts, incidentCategories]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedParentDomain?.length) params.append("parent_domain", selectedParentDomain.join(","));
    if (selectedDomain?.length) params.append("domain", selectedDomain.join(","));
    if (selectedSubDomain?.length) params.append("sub_domain", selectedSubDomain.join(","));
    if (selectedProduct?.length) params.append("product", selectedProduct.join(","));
    if (selectedCategory?.length) params.append("incident_category", selectedCategory.join(","));
    if (searchValue?.trim()) params.append("snowId", searchValue.trim());
    if (resolutionPendingStatus?.trim()) params.append("resolutionPendingStatus", resolutionPendingStatus);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const columnMapping = {
      snowId: "snowId",
      incidentDescription: "incidentDescription",
      incidentCategory: "incidentCategory",
      assignedTo: "assignedTo",
      status: "status",
      resolutionPendingStatus: "resolutionPendingStatus",
      createdAt: "createdAt",
    };
    const activeColumnSearch = Object.entries(columnSearch).find(([, v]) => v && v.trim());
    if (activeColumnSearch) {
      const [frontendCol, val] = activeColumnSearch;
      const apiCol = columnMapping[frontendCol];
      if (apiCol) {
        params.append("searchColumn", apiCol);
        params.append("searchValue", val.trim());
      }
    }
    return params;
  }, [selectedParentDomain, selectedDomain, selectedSubDomain, selectedProduct, selectedCategory, searchValue, resolutionPendingStatus, startDate, endDate, columnSearch]);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.append("page", 1);
      params.append("page_size", INITIAL_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-incidents?${params.toString()}`,
      );

      if (response.data?.status === 200) {
        const normalizedIncidents = (response.data.data || []).map(
          (incident) => ({
            ...incident,
            displayIncidentCategory: formatIncidentCategory(incident.incidentCategory),
          }),
        );

        const normalizedCategoryCounts = Object.entries(
          response.data.incidentCategoryCounts || {},
        ).reduce((acc, [key, value]) => {
          const label = formatIncidentCategory(key);
          acc[label] = (acc[label] || 0) + value;
          return acc;
        }, {});

        const serverTotal = response.data.pagination?.totalCount || 0;
        setIncidents(normalizedIncidents);
        loadedCountRef.current = normalizedIncidents.length;
        setTotalCount(serverTotal);
        setHasMore(serverTotal > normalizedIncidents.length);
        loadMorePageRef.current = Math.ceil(INITIAL_SIZE / MORE_SIZE) + 1;
        setIncidentCategories(response.data.incidentCategories || []);
        setIncidentCategoryCounts(normalizedCategoryCounts);
        setFilters(response.data.filters || null);
      }
    } catch (error) {
      console.error("Error fetching incidents:", error);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMoreIncidents = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    try {
      const params = buildParams();
      params.append("page", loadMorePageRef.current);
      params.append("page_size", MORE_SIZE);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-incidents?${params.toString()}`,
      );

      if (response.data?.status === 200) {
        const newIncidents = (response.data.data || []).map((incident) => ({
          ...incident,
          displayIncidentCategory: formatIncidentCategory(incident.incidentCategory),
        }));
        const serverTotal = response.data.pagination?.totalCount || 0;
        setIncidents((prev) => [...prev, ...newIncidents]);
        loadedCountRef.current += newIncidents.length;
        setHasMore(loadedCountRef.current < serverTotal);
        setTotalCount(serverTotal);
        loadMorePageRef.current += 1;
      }
    } catch (error) {
      console.error("Failed to load more incidents:", error);
    } finally {
      setIsFetchingMore(false);
    }
  }, [isFetchingMore, hasMore, buildParams]);

  useEffect(() => {
    fetchIncidents();
  }, []);

  useEffect(() => {
    if (!filters) return;
    const id = setTimeout(() => {
      fetchIncidents();
    }, 500);
    return () => clearTimeout(id);
  }, [columnSearch]);

  useEffect(() => {
    if (resetTrigger) {
      fetchIncidents();
      setResetTrigger(false);
    }
  }, [resetTrigger]);

  useEffect(() => {
    if (pendingFetch) {
      fetchIncidents();
      setPendingFetch(false);
    }
  }, [pendingFetch, fetchIncidents]);

  const handleDrawerApply = (drawerFilters) => {
    setSelectedParentDomain(drawerFilters.parentDomain || []);
    setSelectedDomain(drawerFilters.domain || []);
    setSelectedSubDomain(drawerFilters.subDomain || []);
    setSelectedProduct(drawerFilters.product || []);
    setSelectedCategory(drawerFilters.category || []);
    setStartDate(drawerFilters.startDate || "");
    setEndDate(drawerFilters.endDate || "");
    setPendingFetch(true);
  };

  const resetAll = () => {
    setSelectedParentDomain([]);
    setSelectedDomain([]);
    setSelectedSubDomain([]);
    setSelectedProduct([]);
    setSelectedCategory([]);
    setSearchValue("");
    setSortColumn("");
    setSortOrder("asc");
    setStartDate("");
    setEndDate("");
    setResolutionPendingStatus("");
    setColumnSearch({
      snowId: "",
      incidentDescription: "",
      incidentCategory: "",
      assignedTo: "",
      status: "",
      resolutionPendingStatus: "",
      createdAt: "",
    });
    setResetTrigger(true);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleColumnSearchChange = (column, value) => {
    if (column !== "resolutionPendingStatus") {
      setResolutionPendingStatus("");
    }

    setColumnSearch({
      snowId: "",
      incidentDescription: "",
      incidentCategory: "",
      assignedTo: "",
      status: "",
      resolutionPendingStatus: "",
      createdAt: "",
      [column]: value,
    });
  };

  const activeFilterCount = [
    ...(selectedParentDomain || []),
    ...(selectedDomain || []),
    ...(selectedSubDomain || []),
    ...(selectedProduct || []),
    ...(selectedCategory || []),
    startDate,
    endDate,
  ].filter(Boolean).length;

  const appliedChips = [
    ...(selectedParentDomain || []).map((v) => ({ label: "Parent Domain", value: v })),
    ...(selectedDomain || []).map((v) => ({ label: "Domain", value: v })),
    ...(selectedSubDomain || []).map((v) => ({ label: "Sub Domain", value: v })),
    ...(selectedProduct || []).map((v) => ({ label: "Product", value: v })),
    ...(selectedCategory || []).map((v) => ({ label: "Category", value: formatIncidentCategory(v) })),
    ...(startDate ? [{ label: "Start Date", value: startDate }] : []),
    ...(endDate ? [{ label: "End Date", value: endDate }] : []),
  ];

  if (loading && incidents.length === 0) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px]">
            Hang tight, fetching data....
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="incident-wrapper">
      <div className="flex flex-col pb-4">
        <h1 className="text-[24px] font-bold">Incident Management</h1>
        <div className="text-[14px] text-gray-600">
          Find your all incidents
        </div>
      </div>

      <div>
        <IncidentPieChart categoryCount={incidentCategoryCounts} />

        <div className="mt-2">
          <div className="py-2">
            <div className="flex items-center justify-end mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[45px] whitespace-nowrap bg-[#bb1ccc] text-white border-[#bb1ccc] hover:bg-[#4f52d1]"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 leading-none font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={resetAll}
                  className="px-4 py-2.5 text-sm font-medium text-white border rounded-lg bg-[#bb1ccc] hover:bg-[#4f52d1] flex items-center gap-2 h-[45px] whitespace-nowrap"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
              </div>
            </div>

            {appliedChips.length > 0 && (
              <div className="mb-4">
                <AppliedFilters chips={appliedChips} />
              </div>
            )}
          </div>

          <IncidentTable
            loading={loading}
            incidents={incidents}
            selectedParentDomain={(selectedParentDomain || []).join(",")}
            selectedDomain={(selectedDomain || []).join(",")}
            selectedSubDomain={(selectedSubDomain || []).join(",")}
            selectedProduct={(selectedProduct || []).join(",")}
            searchValue={searchValue}
            selectedCategory={(selectedCategory || []).join(",")}
            resetAll={resetAll}
            totalCount={totalCount}
            loadMore={loadMoreIncidents}
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            handleSort={handleSort}
            columnSearch={columnSearch}
            onColumnSearchChange={handleColumnSearchChange}
            setResolutionPendingStatus={setResolutionPendingStatus}
          />
        </div>
      </div>

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{
          parentDomain: selectedParentDomain || [],
          domain: selectedDomain || [],
          subDomain: selectedSubDomain || [],
          product: selectedProduct || [],
          category: selectedCategory || [],
          startDate: startDate || null,
          endDate: endDate || null,
        }}
        onApply={handleDrawerApply}
        onReset={resetAll}
        flat
        subtitle="Filter incidents by domain, product & date"
      />
    </div>
  );
};

export default ActionTable;
